import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { getVoiceParts } from '../utils/permissionUtils';
import '../styles/user-profile.css';

const UserProfile = ({ isOpen, onClose, onProfileUpdate, editingUserId }) => {
  const { user } = useAuth();
  const { t } = useLanguage();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [profile, setProfile] = useState({
    displayName: '',
    email: '',
    profilePhotoUrl: null,
    voicePart: '',
    bio: ''
  });
  const [editingUserData, setEditingUserData] = useState(null);
  const fileInputRef = useRef(null);
  
  // Determine which user we're editing
  const targetUserId = editingUserId || user?.id;
  const isEditingOtherUser = editingUserId && editingUserId !== user?.id;

  useEffect(() => {
    if (isOpen && user && targetUserId) {
      loadProfile();
    }
  }, [isOpen, user, targetUserId]);

  const loadProfile = async () => {
    if (!targetUserId) return;
    
    setLoading(true);
    try {
      const API_BASE = process.env.REACT_APP_API_BASE_URL || '';
      const response = await fetch(`${API_BASE}/api/user?operation=getAccount&userId=${targetUserId}`);
      
      if (response.ok) {
        const data = await response.json();
        setEditingUserData(data);
        
        setProfile({
          displayName: data.displayName || (isEditingOtherUser ? '' : user.name) || '',
          email: data.email || (isEditingOtherUser ? '' : user.email) || '',
          profilePhotoUrl: data.profilePhotoUrl || null,
          voicePart: data.voicePart || '',
          bio: data.bio || ''
        });
      } else if (response.status === 404 && !isEditingOtherUser) {
        // Account doesn't exist yet for current user, create it with Google data
        await createInitialProfile();
      } else if (response.status === 404 && isEditingOtherUser) {
        // Other user doesn't have profile data yet, show empty form
        setProfile({
          displayName: '',
          email: '',
          profilePhotoUrl: null,
          voicePart: '',
          bio: ''
        });
      } else {
        throw new Error('Failed to load profile');
      }
    } catch (error) {
      console.error('Failed to load profile:', error);
      // Fallback data
      if (isEditingOtherUser) {
        setProfile({
          displayName: '',
          email: '',
          profilePhotoUrl: null,
          voicePart: '',
          bio: ''
        });
      } else {
        setProfile({
          displayName: user.name || '',
          email: user.email || '',
          profilePhotoUrl: null,
          voicePart: '',
          bio: ''
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const createInitialProfile = async () => {
    if (!user?.id) return;

    try {
      const API_BASE = process.env.REACT_APP_API_BASE_URL || '';
      const response = await fetch(`${API_BASE}/api/user?operation=createAccount&userId=${user.id}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          displayName: user.name || '',
          email: user.email || '',
          googleImageUrl: user.image || null
        }),
      });

      if (response.ok) {
        const data = await response.json();
        setProfile({
          displayName: data.data.displayName || '',
          email: data.data.email || '',
          profilePhotoUrl: data.data.profilePhotoUrl || null,
          voicePart: data.data.voicePart || '',
          bio: data.data.bio || ''
        });
      }
    } catch (error) {
      console.error('Failed to create initial profile:', error);
    }
  };

  const handleSave = async () => {
    if (!targetUserId) return;

    setSaving(true);
    try {
      const API_BASE = process.env.REACT_APP_API_BASE_URL || '';
      const response = await fetch(`${API_BASE}/api/user?operation=updateAccount&userId=${targetUserId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...profile,
          googleImageUrl: isEditingOtherUser ? null : (user.image || null),
          editedBy: user.id // Track who made the edit
        }),
      });

      if (response.ok) {
        const result = await response.json();
        
        // Notify parent component to refresh profile data immediately
        if (onProfileUpdate) {
          await onProfileUpdate();
        }
        alert(t(isEditingOtherUser ? 'profile.userUpdateSuccess' : 'profile.updateSuccess'));
        onClose();
      } else {
        const errorData = await response.json();
        console.error('API Error:', errorData);
        throw new Error(`Failed to update profile: ${errorData.details || errorData.error}`);
      }
    } catch (error) {
      console.error('Failed to save profile:', error);
      alert(t('profile.updateFailed'));
    } finally {
      setSaving(false);
    }
  };

  const handleInputChange = (field, value) => {
    setProfile(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handlePhotoUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      alert(t('profile.invalidFileType'));
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      alert(t('profile.fileTooLarge'));
      return;
    }

    setUploadingPhoto(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('userId', targetUserId);
      formData.append('folder', 'profile-photos');

      const API_BASE = process.env.REACT_APP_API_BASE_URL || '';
      const response = await fetch(`${API_BASE}/api/profile-photo`, {
        method: 'POST',
        body: formData,
      });

      if (response.ok) {
        const data = await response.json();
        setProfile(prev => ({
          ...prev,
          profilePhotoUrl: data.url
        }));
      } else {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to upload photo');
      }
    } catch (error) {
      console.error('Failed to upload photo:', error);
      alert(t('profile.photoUploadFailed'));
    } finally {
      setUploadingPhoto(false);
    }
  };

  const handlePhotoRemove = () => {
    setProfile(prev => ({
      ...prev,
      profilePhotoUrl: null
    }));
  };

  const getProfilePhotoUrl = () => {
    return profile.profilePhotoUrl || (isEditingOtherUser ? null : user?.image);
  };

  if (!isOpen) return null;

  return (
    <div className="user-profile-overlay">
      <div className="user-profile-modal">
        <div className="user-profile-header">
          <div className="header-content">
            <div className="header-icon">👤</div>
            <div className="header-text">
              <h2>{isEditingOtherUser ? t('profile.editUserTitle') : t('profile.title')}</h2>
              <p>{isEditingOtherUser ? t('profile.editUserSubtitle') : t('profile.subtitle')}</p>
            </div>
          </div>
          <button className="close-button" onClick={onClose}>×</button>
        </div>

        <div className="user-profile-content">
          {loading ? (
            <div className="loading-state">
              <div className="loading-spinner"></div>
              <p>{t('profile.loadingProfile')}</p>
            </div>
          ) : (
            <>
              {/* Photo Section */}
              <div className="photo-section">
                <div className="photo-container">
                  <div className="photo-wrapper">
                    <img 
                      src={getProfilePhotoUrl()} 
                      alt={profile.displayName || (isEditingOtherUser ? 'User' : user?.name)} 
                      className="profile-photo"
                    />
                    {uploadingPhoto && (
                      <div className="photo-upload-overlay">
                        <div className="upload-spinner"></div>
                      </div>
                    )}
                  </div>
                  
                  <div className="photo-controls">
                    <input
                      type="file"
                      ref={fileInputRef}
                      onChange={handlePhotoUpload}
                      accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
                      style={{ display: 'none' }}
                    />
                    <button 
                      className="photo-upload-btn"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={uploadingPhoto || saving}
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20Z" />
                      </svg>
                      {uploadingPhoto ? t('profile.uploading') : t('profile.uploadPhoto')}
                    </button>
                    
                    {profile.profilePhotoUrl && (
                      <button 
                        className="photo-remove-btn"
                        onClick={handlePhotoRemove}
                        disabled={uploadingPhoto || saving}
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M19,4H15.5L14.5,3H9.5L8.5,4H5V6H19M6,19A2,2 0 0,0 8,21H16A2,2 0 0,0 18,19V7H6V19Z" />
                        </svg>
                        {t('profile.removePhoto')}
                      </button>
                    )}
                  </div>
                </div>
                
                {!isEditingOtherUser && (
                  <div className="google-info">
                    <p className="google-account">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
                        <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                        <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                        <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                      </svg>
                      {t('profile.googleAccount', { email: user?.email })}
                    </p>
                  </div>
                )}
              </div>

              {/* Form Section */}
              <div className="form-section">
                <div className="form-group">
                  <label htmlFor="displayName">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/>
                    </svg>
                    {t('profile.displayName')}
                  </label>
                  <input
                    type="text"
                    id="displayName"
                    value={profile.displayName}
                    onChange={(e) => handleInputChange('displayName', e.target.value)}
                    placeholder={t('profile.displayNamePlaceholder')}
                    disabled={saving}
                  />
                  <div className="field-help">
                    {t('profile.displayNameHelp')}
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="email">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M20,8L12,13L4,8V6L12,11L20,6M20,4H4C2.89,4 2,4.89 2,6V18A2,2 0 0,0 4,20H20A2,2 0 0,0 22,18V6C22,4.89 21.1,4 20,4Z" />
                    </svg>
                    {t('profile.email')}
                  </label>
                  <input
                    type="email"
                    id="email"
                    value={profile.email}
                    onChange={(e) => handleInputChange('email', e.target.value)}
                    placeholder={t('profile.emailPlaceholder')}
                    disabled={saving}
                  />
                  <div className="field-help">
                    {t('profile.emailHelp')}
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="voicePart">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M12,3V12.26C11.5,12.09 11,12 10.5,12C8,12 6,14 6,16.5C6,19 8,21 10.5,21C13,21 15,19 15,16.5V6H19V3H12Z" />
                    </svg>
                    {t('profile.voicePart')}
                  </label>
                  <select
                    id="voicePart"
                    value={profile.voicePart}
                    onChange={(e) => handleInputChange('voicePart', e.target.value)}
                    disabled={saving}
                  >
                    <option value="">{t('profile.voicePartPlaceholder')}</option>
                    {getVoiceParts().map(part => (
                      <option key={part.value} value={part.value}>
                        {part.icon} {t(`profile.voiceParts.${part.value}`)}
                      </option>
                    ))}
                  </select>
                  <div className="field-help">
                    {t('profile.voicePartHelp')}
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="bio">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20Z" />
                    </svg>
                    {t('profile.bio')}
                  </label>
                  <textarea
                    id="bio"
                    value={profile.bio}
                    onChange={(e) => handleInputChange('bio', e.target.value)}
                    placeholder={t('profile.bioPlaceholder')}
                    disabled={saving}
                    rows={4}
                    maxLength={500}
                  />
                  <div className="field-help">
                    {t('profile.bioHelp')}
                    {profile.bio && (
                      <span className="char-count"> ({profile.bio.length}/500)</span>
                    )}
                  </div>
                </div>
              </div>

              {/* Actions */}
              <div className="profile-actions">
                <button 
                  onClick={onClose} 
                  className="cancel-button"
                  disabled={saving || uploadingPhoto}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M19,6.41L17.59,5L12,10.59L6.41,5L5,6.41L10.59,12L5,17.59L6.41,19L12,13.41L17.59,19L19,17.59L13.41,12L19,6.41Z" />
                  </svg>
                  {t('controls.cancel')}
                </button>
                <button 
                  onClick={handleSave} 
                  className="save-button"
                  disabled={saving || uploadingPhoto}
                >
                  {saving ? (
                    <>
                      <div className="button-spinner"></div>
                      {t('profile.saving')}
                    </>
                  ) : (
                    <>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M21,7L9,19L3.5,13.5L4.91,12.09L9,16.17L19.59,5.59L21,7Z" />
                      </svg>
                      {t('profile.saveChanges')}
                    </>
                  )}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default UserProfile;